// src/Studio/engine/keyframeEngine.ts
// Advanced keyframe animation engine for ViewsBoost Studio
// Supports multi-property keyframes, custom easing curves, and complex animations

import type { Element } from "../types";

// ==================== KEYFRAME TYPES ====================

export type AnimatableProperty =
  | "x"
  | "y"
  | "scaleX"
  | "scaleY"
  | "rotation"
  | "opacity"
  | "blur"
  | "brightness"
  | "contrast"
  | "saturation"
  | "pathProgress" // Motion path progress 0-1 (Phase 5)
  // Text animation properties
  | "textReveal" // Text reveal progress 0-1
  | "textAnimTranslateY" // Per-unit Y offset for text animation
  | "textAnimTranslateX" // Per-unit X offset for text animation
  | "textAnimScale" // Per-unit scale for text animation
  | "textAnimOpacity"; // Per-unit opacity for text animation

export type EasingFunction = (t: number) => number;

export type BezierEasing = {
  type: "bezier";
  x1: number; // Control point 1 x (0-1)
  y1: number; // Control point 1 y (can be outside 0-1)
  x2: number; // Control point 2 x (0-1)
  y2: number; // Control point 2 y (can be outside 0-1)
};

export type SpringEasing = {
  type: "spring";
  tension: number; // 0-1000 (default: 170)
  friction: number; // 0-100 (default: 26)
  mass: number; // 0.1-10 (default: 1)
};

export type ElasticEasing = {
  type: "elastic";
  amplitude: number; // 0.1-2 (default: 1)
  period: number; // 0-2 (default: 0.3)
};

export type BounceEasing = {
  type: "bounce";
  bounces: number; // 1-5 (default: 4)
  stiffness: number; // 0.1-2 (default: 1)
};

export type EasingType =
  | "linear"
  | "easeIn"
  | "easeOut"
  | "easeInOut"
  | "easeInCubic"
  | "easeOutCubic"
  | "easeInOutCubic"
  | "easeInBack"
  | "easeOutBack"
  | "easeInOutBack"
  | BezierEasing
  | SpringEasing
  | ElasticEasing
  | BounceEasing;

export interface Keyframe {
  time: number; // Time in seconds (relative to element start)
  value: number; // Value at this time
  easing: EasingType; // Easing function to next keyframe
}

export interface PropertyTrack {
  property: AnimatableProperty;
  keyframes: Keyframe[];
}

export interface AnimationTrack {
  elementId: string;
  tracks: PropertyTrack[];
}

// ==================== EASING FUNCTIONS ====================

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

function clamp01(n: number): number {
  return clamp(n, 0, 1);
}

/**
 * Basic easing functions
 */
export const EASING_FUNCTIONS: Record<string, EasingFunction> = {
  linear: (t: number) => t,

  easeIn: (t: number) => t * t,
  easeOut: (t: number) => 1 - (1 - t) * (1 - t),
  easeInOut: (t: number) =>
    t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2,

  easeInCubic: (t: number) => t * t * t,
  easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  easeInOutCubic: (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,

  easeInQuart: (t: number) => t * t * t * t,
  easeOutQuart: (t: number) => 1 - Math.pow(1 - t, 4),
  easeInOutQuart: (t: number) =>
    t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2,

  easeInBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return c3 * t * t * t - c1 * t * t;
  },

  easeOutBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },

  easeInOutBack: (t: number) => {
    const c1 = 1.70158;
    const c2 = c1 * 1.525;
    return t < 0.5
      ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
      : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2;
  },
};

/**
 * Cubic bezier easing (like CSS cubic-bezier())
 */
function cubicBezier(x1: number, y1: number, x2: number, y2: number): EasingFunction {
  // Simplified Newton-Raphson method for finding t given x
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;

    // Binary search for t value
    let t = x;
    let iterations = 0;
    const maxIterations = 10;

    while (iterations < maxIterations) {
      const currentX = 3 * (1 - t) * (1 - t) * t * x1 + 3 * (1 - t) * t * t * x2 + t * t * t;
      const error = currentX - x;

      if (Math.abs(error) < 0.001) break;

      // Derivative
      const dx = 3 * (1 - t) * (1 - t) * x1 + 6 * (1 - t) * t * (x2 - x1) + 3 * t * t * (1 - x2);

      if (Math.abs(dx) < 0.000001) break;

      t -= error / dx;
      t = clamp01(t);
      iterations++;
    }

    // Calculate y for this t
    return 3 * (1 - t) * (1 - t) * t * y1 + 3 * (1 - t) * t * t * y2 + t * t * t;
  };
}

/**
 * Elastic easing (bouncy oscillation)
 */
function elastic(amplitude: number, period: number, out: boolean): EasingFunction {
  const a = clamp(amplitude, 0.1, 2);
  const p = clamp(period, 0, 2);

  return (t: number) => {
    if (t === 0 || t === 1) return t;

    const s = (p / (2 * Math.PI)) * Math.asin(1 / a);
    const adjust = out ? t : 1 - t;

    return out
      ? a * Math.pow(2, -10 * adjust) * Math.sin(((adjust - s) * (2 * Math.PI)) / p) + 1
      : -(a * Math.pow(2, 10 * (adjust - 1)) * Math.sin(((adjust - 1 - s) * (2 * Math.PI)) / p));
  };
}

/**
 * Bounce easing (bouncing ball effect)
 */
function bounce(bounces: number, stiffness: number, out: boolean): EasingFunction {
  const n = clamp(Math.round(bounces), 1, 5);
  const s = clamp(stiffness, 0.1, 2);

  return (t: number) => {
    const adjust = out ? t : 1 - t;

    if (adjust < 4 / 11) {
      return out ? 7.5625 * adjust * adjust : 1 - 7.5625 * adjust * adjust;
    } else if (adjust < 8 / 11) {
      const t2 = adjust - 6 / 11;
      return out ? 7.5625 * t2 * t2 + 0.75 : 1 - (7.5625 * t2 * t2 + 0.75);
    } else if (adjust < 10 / 11) {
      const t2 = adjust - 9 / 11;
      return out ? 7.5625 * t2 * t2 + 0.9375 : 1 - (7.5625 * t2 * t2 + 0.9375);
    } else {
      const t2 = adjust - 21 / 22;
      return out ? 7.5625 * t2 * t2 + 0.984375 : 1 - (7.5625 * t2 * t2 + 0.984375);
    }
  };
}

/**
 * Spring easing (physics-based spring motion)
 */
function spring(tension: number, friction: number, mass: number): EasingFunction {
  const t = clamp(tension, 0, 1000);
  const f = clamp(friction, 0, 100);
  const m = clamp(mass, 0.1, 10);

  // Damped harmonic oscillator
  const w0 = Math.sqrt(t / m);
  const zeta = f / (2 * Math.sqrt(t * m));

  if (zeta < 1) {
    // Underdamped (oscillates)
    const wd = w0 * Math.sqrt(1 - zeta * zeta);
    return (time: number) => {
      return 1 - Math.exp(-zeta * w0 * time) * Math.cos(wd * time);
    };
  } else if (zeta > 1) {
    // Overdamped (slow)
    const r = zeta * w0;
    return (time: number) => {
      return 1 - Math.exp(-r * time);
    };
  } else {
    // Critically damped (fastest non-oscillating)
    return (time: number) => {
      return 1 - (1 + w0 * time) * Math.exp(-w0 * time);
    };
  }
}

/**
 * Get easing function from easing type
 */
export function getEasingFunction(easing: EasingType): EasingFunction {
  if (typeof easing === "string") {
    return EASING_FUNCTIONS[easing] || EASING_FUNCTIONS.linear;
  }

  if (easing.type === "bezier") {
    return cubicBezier(easing.x1, easing.y1, easing.x2, easing.y2);
  }

  if (easing.type === "elastic") {
    return elastic(easing.amplitude, easing.period, true);
  }

  if (easing.type === "bounce") {
    return bounce(easing.bounces, easing.stiffness, true);
  }

  if (easing.type === "spring") {
    return spring(easing.tension, easing.friction, easing.mass);
  }

  return EASING_FUNCTIONS.linear;
}

// ==================== KEYFRAME INTERPOLATION ====================

/**
 * Find the two keyframes that surround the given time
 */
function findKeyframeSegment(keyframes: Keyframe[], time: number): {
  before: Keyframe | null;
  after: Keyframe | null;
} {
  if (keyframes.length === 0) {
    return { before: null, after: null };
  }

  // Sort keyframes by time
  const sorted = [...keyframes].sort((a, b) => a.time - b.time);

  // Before first keyframe
  if (time <= sorted[0].time) {
    return { before: null, after: sorted[0] };
  }

  // After last keyframe
  if (time >= sorted[sorted.length - 1].time) {
    return { before: sorted[sorted.length - 1], after: null };
  }

  // Find surrounding keyframes
  for (let i = 0; i < sorted.length - 1; i++) {
    if (time >= sorted[i].time && time <= sorted[i + 1].time) {
      return { before: sorted[i], after: sorted[i + 1] };
    }
  }

  return { before: null, after: null };
}

/**
 * Interpolate value between two keyframes
 */
export function interpolateKeyframes(
  track: PropertyTrack,
  time: number,
  defaultValue: number
): number {
  if (!track || track.keyframes.length === 0) {
    return defaultValue;
  }

  const { before, after } = findKeyframeSegment(track.keyframes, time);

  // Before first keyframe: use first keyframe value
  if (!before && after) {
    return after.value;
  }

  // After last keyframe: use last keyframe value
  if (before && !after) {
    return before.value;
  }

  // Between keyframes: interpolate
  if (before && after) {
    const duration = after.time - before.time;
    if (duration <= 0) return before.value;

    const t = (time - before.time) / duration;
    const easingFn = getEasingFunction(before.easing);
    const easedT = clamp01(easingFn(t));

    return before.value + (after.value - before.value) * easedT;
  }

  return defaultValue;
}

/**
 * Get animated value for a property at a given time
 */
export function getAnimatedPropertyValue(
  tracks: PropertyTrack[],
  property: AnimatableProperty,
  time: number,
  defaultValue: number
): number {
  const track = tracks.find((t) => t.property === property);
  if (!track) return defaultValue;

  return interpolateKeyframes(track, time, defaultValue);
}

/**
 * Get all animated properties at a given time
 */
export function getAnimatedValues(
  tracks: PropertyTrack[],
  time: number,
  element: Element
): Partial<Record<AnimatableProperty, number>> {
  const result: Partial<Record<AnimatableProperty, number>> = {};

  const defaults: Record<AnimatableProperty, number> = {
    x: element.x,
    y: element.y,
    scaleX: element.scaleX ?? 1,
    scaleY: element.scaleY ?? 1,
    rotation: element.rotation ?? 0,
    opacity: element.opacity ?? 1,
    blur: element.blur ?? 0,
    brightness: 1,
    contrast: 1,
    saturation: 1,
    pathProgress: 0, // Motion path progress default
    // Text animation defaults
    textReveal: 1, // Fully visible by default
    textAnimTranslateY: 0,
    textAnimTranslateX: 0,
    textAnimScale: 1,
    textAnimOpacity: 1,
  };

  for (const track of tracks) {
    const value = interpolateKeyframes(track, time, defaults[track.property]);
    result[track.property] = value;
  }

  return result;
}

// ==================== KEYFRAME MANAGEMENT ====================

/**
 * Add a keyframe to a property track
 */
export function addKeyframe(
  track: PropertyTrack,
  keyframe: Keyframe
): PropertyTrack {
  const existingIndex = track.keyframes.findIndex((k) => k.time === keyframe.time);

  if (existingIndex >= 0) {
    // Update existing keyframe
    const newKeyframes = [...track.keyframes];
    newKeyframes[existingIndex] = keyframe;
    return { ...track, keyframes: newKeyframes };
  } else {
    // Add new keyframe
    return { ...track, keyframes: [...track.keyframes, keyframe] };
  }
}

/**
 * Remove a keyframe from a property track
 */
export function removeKeyframe(
  track: PropertyTrack,
  time: number
): PropertyTrack {
  return {
    ...track,
    keyframes: track.keyframes.filter((k) => k.time !== time),
  };
}

/**
 * Move a keyframe to a new time
 */
export function moveKeyframe(
  track: PropertyTrack,
  oldTime: number,
  newTime: number
): PropertyTrack {
  const keyframe = track.keyframes.find((k) => k.time === oldTime);
  if (!keyframe) return track;

  const withoutOld = removeKeyframe(track, oldTime);
  return addKeyframe(withoutOld, { ...keyframe, time: newTime });
}

/**
 * Update keyframe easing
 */
export function updateKeyframeEasing(
  track: PropertyTrack,
  time: number,
  easing: EasingType
): PropertyTrack {
  const keyframe = track.keyframes.find((k) => k.time === time);
  if (!keyframe) return track;

  return addKeyframe(track, { ...keyframe, easing });
}

/**
 * Copy keyframes from one track to another
 */
export function copyKeyframes(
  source: PropertyTrack,
  timeOffset: number = 0
): PropertyTrack {
  return {
    ...source,
    keyframes: source.keyframes.map((k) => ({
      ...k,
      time: k.time + timeOffset,
    })),
  };
}
